import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import OTPLoginUI from "./OTPLoginUI";
import { useAuth } from "../../services/auth";
import { resolveReturnPath } from "../../utils/authRedirect";

// RequestOTPView deletes the previous unused OTP whenever it issues a new one,
// and a code stays valid for OTP.OTP_EXPIRY_MINUTES (2). Offering a resend at
// the 60s *cooldown* therefore lets a user invalidate a code that is still
// good: two SMS land in the thread, and entering the older one returns
// "کد نامعتبر است" for a code that looks perfectly valid. Wait out the code's
// own lifetime, so a resend can only ever replace a code that is already dead.
const CODE_LIFETIME_SECONDS = 120;

// DRF's scoped throttle (otp_request/otp_verify) answers a bare 429 with no
// retry_after_seconds; hold the UI back a bit anyway so it cannot hammer the
// endpoint while the rate limit window is still open.
const THROTTLE_FALLBACK_SECONDS = 30;




function extractOtpRawMessage(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "";
  if (typeof data === "string") return data;
  if (data.error) return Array.isArray(data.error) ? data.error[0] : data.error;
  if (data.detail)
    return Array.isArray(data.detail) ? data.detail[0] : data.detail;
  if (data.message)
    return Array.isArray(data.message) ? data.message[0] : data.message;
  if (data.non_field_errors)
    return Array.isArray(data.non_field_errors)
      ? data.non_field_errors[0]
      : data.non_field_errors;
  const firstKey = Object.keys(data)[0];
  if (firstKey && Array.isArray(data[firstKey])) return data[firstKey][0];
  return "";
}

// The two 429 shapes are not interchangeable:
//  - `retry_after_seconds` comes from the views themselves. On request it is the
//    60s send cooldown, which means a code *was* sent; on verify it is the
//    phone-scoped lockout.
//  - `available_in_seconds` comes from DRF's ScopedRateThrottle via
//    users.exceptions.custom_exception_handler: a raw rate limit where no code
//    was sent at all.
const getCooldownSeconds = (err) => {
  const seconds = Number(err?.response?.data?.retry_after_seconds);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
};

const getThrottleSeconds = (err) => {
  const seconds = Number(err?.response?.data?.available_in_seconds);
  return Number.isFinite(seconds) && seconds > 0 ? Math.ceil(seconds) : 0;
};

function isThrottled(err) {
  const status = err?.response?.status;
  const raw = String(extractOtpRawMessage(err) || "").toLowerCase();
  return (
    status === 429 ||
    raw.includes("too many") ||
    raw.includes("throttl") ||
    raw.includes("try again later")
  );
}

function getOtpErrorMessage(err, fallback, throttleFallback) {
  const raw = extractOtpRawMessage(err);
  const text = String(raw || "");

  // Backend already localized — keep it.
  if (/[؀-ۿ]/.test(text)) return text;

  if (isThrottled(err)) {
    return (
      throttleFallback ||
      "تعداد تلاش‌های شما بیش از حد مجاز است. لطفاً چند دقیقه دیگر تلاش کنید."
    );
  }

  return fallback;
}

export default function OTPLogin() {
  const navigate = useNavigate();
  const location = useLocation();

  // `from` may be a string (MenuDrawer/Header) or a location object (ProtectedRoute legacy).
  const from = resolveReturnPath(location.state?.from, "/");
  const { requestOTP, verifyOTP, isAuthenticated } = useAuth();

  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(0);
  // Phone-scoped lockout left by the backend (5 wrong guesses → 15 minutes).
  const [lockSeconds, setLockSeconds] = useState(0);
  // A resend invalidates the code sitting in the SMS thread (RequestOTPView
  // deletes the unused row), so the UI has to say so or the user retypes the
  // old code and gets "کد نامعتبر است" for something that looks valid.
  const [codeReplaced, setCodeReplaced] = useState(false);

  const isLocked = lockSeconds > 0;

  // Two verify POSTs for the same code are not idempotent: the first consumes
  // the OTP row, so the second finds nothing unused and comes back 400
  // "کد نامعتبر است" — a wrong-code error for a code that was just accepted.
  // `loading` lives in state and is stale inside an async callback, so the
  // in-flight flag has to be a ref to actually close that window.
  const inFlightRef = useRef(false);

  // Keep a ref to 'from' so it never becomes stale in async callbacks
  const fromRef = useRef(from);
  useEffect(() => {
    fromRef.current = from;
  }, [from]);

  // If already logged in before mounting (e.g., direct navigation to /login while authenticated)
  useEffect(() => {
    if (isAuthenticated && !loading) {
      navigate(fromRef.current, { replace: true });
    }
  }, [isAuthenticated, navigate, loading]);

  // One interval drives both countdowns so a lockout started mid-resend still
  // ticks down at the same rate as the send cooldown.
  useEffect(() => {
    if (timer <= 0 && lockSeconds <= 0) return undefined;

    const interval = setInterval(() => {
      setTimer((prev) => Math.max(prev - 1, 0));
      setLockSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer, lockSeconds]);

  const handleRequestOTP = useCallback(async (isResend = false) => {
    if (loading || isLocked || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      setLoading(true);
      setError("");

      await requestOTP(`0${phone}`);

      setTimer(CODE_LIFETIME_SECONDS);
      setCodeReplaced(isResend);
      setStep(2);
    } catch (err) {
      const cooldown = getCooldownSeconds(err);

      // Only the view-level cooldown means a code is already on its way, so
      // move to step 2 and let the server's remaining seconds drive the resend
      // timer. A bare rate-limit 429 means nothing was sent — stay on step 1.
      if (cooldown > 0 && isThrottled(err)) {
        setTimer(Math.max(cooldown, CODE_LIFETIME_SECONDS));
        setStep(2);
      }

      setError(
        getOtpErrorMessage(
          err,
          !err?.response
            ? "ارتباط با سرور برقرار نشد. اتصال اینترنت خود را بررسی کنید."
            : err.response.status === 400
              ? "شماره همراه وارد شده معتبر نیست."
              : "خطا در ارسال کد تایید. مجدداً تلاش کنید.",
          "کد تایید همین چند لحظه پیش ارسال شده است. لطفاً تا پایان زمان انتظار صبر کنید."
        )
      );
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [phone, loading, isLocked, requestOTP]);

  const handleVerifyOTP = useCallback(async () => {
    if (loading || isLocked || inFlightRef.current) return;
    inFlightRef.current = true;
    const destination = fromRef.current; // Store target path before API call
    try {
      setLoading(true);
      setError("");

      await verifyOTP(`0${phone}`, code);

      // Perform single, explicit redirect after successful OTP verification
      navigate(destination, { replace: true });
    } catch (err) {
      // The code was accepted and the tokens are stored; only the profile
      // lookup that follows failed. Blaming the code here is what makes a
      // successful login look like a rejected one.
      if (err?.otpVerified) {
        setError("کد تایید شد اما دریافت اطلاعات کاربر انجام نشد. لطفاً صفحه را دوباره بارگذاری کنید.");
        return;
      }

      // Only a rejection from the API burns the code server-side. On a network
      // blip the code is still good, so wiping it would force a pointless
      // retype and move the user one step closer to the 5-guess lockout.
      const status = err?.response?.status;
      if (status === 400 || status === 429) {
        setCode("");
      }

      // Lockout (5 wrong guesses) and raw rate limiting both mean "stop"; the
      // view reports how long is left, the throttle only a rough estimate.
      if (isThrottled(err)) {
        setLockSeconds(
          getCooldownSeconds(err) || getThrottleSeconds(err) || THROTTLE_FALLBACK_SECONDS
        );
      }

      setError(
        getOtpErrorMessage(
          err,
          status
            ? "کد وارد شده اشتباه است."
            : "ارتباط با سرور برقرار نشد. کد را دوباره ارسال کنید."
        )
      );
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [phone, code, loading, isLocked, verifyOTP, navigate]);

  const handleEditPhone = useCallback(() => {
    setCode("");
    setError("");
    setTimer(0);
    setLockSeconds(0);
    setCodeReplaced(false);
    setStep(1);
  }, []);

  return (
    <OTPLoginUI
      step={step}
      phone={phone}
      setPhone={setPhone}
      code={code}
      setCode={setCode}
      loading={loading}
      error={error}
      timer={timer}
      locked={isLocked}
      lockSeconds={lockSeconds}
      codeReplaced={codeReplaced}
      requestOTP={handleRequestOTP}
      verifyOTP={handleVerifyOTP}
      handleEditPhone={handleEditPhone}
    />
  );
}